import React, { useState, useEffect } from 'react';
import LORManagerSelector from './LORManagerSelector';
import Icon from '../../components/Icon';

const GRADE_COLORS = {
  elite: '#22c55e', good: '#86efac', neutral: '#94a3b8', tough: '#f97316', brutal: '#ef4444',
};

export default function LORWaivers({ api }) {
  const [rosterId, setRosterId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!rosterId) return;
    setLoading(true);
    setError(null);
    fetch(`${api}/api/lor/intel/waivers/${rosterId}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Could not load waiver data.'); setLoading(false); });
  }, [api, rosterId]);

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title">🚨 Waiver Wire Targets</h2>
        <LORManagerSelector api={api} onSelect={setRosterId} selectedRosterId={rosterId} />
      </div>

      {!rosterId && (
        <div className="lor-intel-empty">
          <div className="lor-intel-empty-icon">🚨</div>
          <p>Select your team to see personalized waiver targets based on your roster needs.</p>
        </div>
      )}

      {rosterId && loading && <div className="lor-loading">Scanning the wire…</div>}
      {rosterId && error && <div className="lor-empty">{error}</div>}

      {data && !loading && (
        <>
          <div className="lor-intel-meta">
            Week {data.current_week} · {data.waiver_type}
            {data.waiver_type === 'FAAB' && <> · Budget: ${data.waiver_budget}</>}
            {' '}· <span className="lor-generated-badge">rules-based</span>
          </div>

          {data.position_needs.length > 0 && (
            <div className="lor-waiver-needs">
              <div className="lor-waiver-needs-label"><Icon emoji="📊" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Position needs diagnosed:</div>
              <div className="lor-waiver-needs-list">
                {data.position_needs.map((n, i) => (
                  <div key={i} className="lor-waiver-need-pill">
                    <span className="lor-waiver-need-pos">{n.position}</span>
                    <span className="lor-waiver-need-reason">{n.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.targets.length === 0 ? (
            <div className="lor-intel-all-clear">
              <div className="lor-intel-all-clear-icon"><Icon emoji="✅" size={28} /></div>
              <p>No strong waiver targets found this week. Your roster is in good shape.</p>
            </div>
          ) : (
            <div className="lor-waiver-targets">
              {data.targets.map((t, i) => (
                <WaiverCard key={i} target={t} rank={i + 1} waiverType={data.waiver_type} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function WaiverCard({ target, rank, waiverType }) {
  const gradeColor = GRADE_COLORS[target.matchup_grade] || '#94a3b8';
  const actionClass = target.action === 'add_now' ? 'add-now' : 'monitor';

  return (
    <div className={`lor-card lor-waiver-card${target.position_need ? ' lor-waiver-card--need' : ''}`}>
      <div className="lor-waiver-card-rank">#{rank}</div>

      <div className="lor-waiver-card-body">
        <div className="lor-waiver-card-top">
          <div className="lor-waiver-player-info">
            <span className="lor-waiver-name">{target.full_name}</span>
            <span className="lor-waiver-pos-team">{target.position} · {target.team}</span>
          </div>
          <div className="lor-waiver-card-right">
            <div className={`lor-waiver-action lor-waiver-action--${actionClass}`}>
              {target.action === 'add_now' ? <><Icon emoji="⚡" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Add Now</> : '👁 Monitor'}
            </div>
          </div>
        </div>

        <div className="lor-waiver-stats-row">
          <div className="lor-waiver-stat">
            <span className="lor-waiver-stat-val">{target.projected}</span>
            <span className="lor-waiver-stat-lbl">Projected</span>
          </div>
          <div className="lor-waiver-stat">
            <span className="lor-waiver-stat-val">
              <span className="lor-waiver-grade-dot" style={{ background: gradeColor }} />
              {target.matchup_grade_label}
            </span>
            <span className="lor-waiver-stat-lbl">Matchup</span>
          </div>
          {target.faab_suggestion && (
            <div className="lor-waiver-stat">
              <span className="lor-waiver-stat-val lor-waiver-faab">{target.faab_suggestion}</span>
              <span className="lor-waiver-stat-lbl">FAAB</span>
            </div>
          )}
          <div className="lor-waiver-stat">
            <span className={`lor-waiver-conf lor-waiver-conf--${target.confidence?.toLowerCase()}`}>
              {target.confidence}
            </span>
            <span className="lor-waiver-stat-lbl">Confidence</span>
          </div>
          <div className="lor-waiver-stat">
            <span className="lor-waiver-stat-val">{target.risk}</span>
            <span className="lor-waiver-stat-lbl">Risk</span>
          </div>
        </div>

        {target.injury_status && (
          <div className="lor-waiver-inj-flag">⚕️ {target.injury_status}</div>
        )}

        <div className="lor-waiver-reasoning">{target.reasoning}</div>
        <div className="lor-generated-tag">⚙️ rules-based</div>
      </div>
    </div>
  );
}
